/* eslint-disable better-tailwindcss/no-unknown-classes */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { CloseIcon } from '@infinex/ui-icons';
import { cn } from '@infinex/ui-styles';
import { Button, Text } from '@infinex/ui/components';

export type BottomSheetModalProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
};

export function BottomSheetModal({
  open,
  title,
  onClose,
  children,
  className = 'w-full rounded-t-3xl shadow-2xl',
}: BottomSheetModalProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (open) {
      setIsVisible(true);
      // Double RAF for smoother animation start
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before hiding
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 350); // Slightly longer for smoother feel
      return () => clearTimeout(timer);
    }
  }, [open]);

  if (!isVisible) return null;

  return createPortal(
    <div
      className="z-dialog fixed inset-0 flex items-end"
      aria-modal="true"
      role="dialog"
    >
      {/* backdrop */}
      <div
        className={cn(
          'absolute inset-0 bg-black/80 transition-opacity duration-350 ease-out',
          isAnimating ? 'opacity-100' : 'opacity-0'
        )}
        onClick={onClose}
        aria-hidden
      />

      {/* sheet */}
      <div
        className={cn(
          'bg-canvas z-dialog absolute right-0 bottom-0 left-0',
          'pb-safe', // add some breathing room on iOS-like envs
          'transition-all duration-350',
          isAnimating
            ? 'translate-y-0 opacity-100'
            : 'translate-y-[20%] opacity-0',
          className
        )}
        style={{
          transitionTimingFunction: isAnimating
            ? 'cubic-bezier(0.32, 0.72, 0, 1)'
            : 'cubic-bezier(0.72, 0, 0.32, 1)',
        }}
      >
        {/* header */}
        <div className="relative flex items-center justify-center px-2 pt-6">
          <Text className="text-emphasis text-base font-semibold">{title}</Text>
          <Button
            aria-label="Close"
            size="small"
            prominence="low"
            onPress={onClose}
            className="absolute top-5 right-4 rounded-full"
          >
            <CloseIcon />
          </Button>
        </div>

        {/* content */}
        <div className="max-h-[65vh] overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body
  );
}
